/**
 * Copyright (c) 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { API_CONFIG } from '../config/api.config';

/**
 * Payload sent from the server when the agent shows a chord
 */
export interface ShowChordPayload {
  root: string;
  chordType: string;
  isChord: boolean;
  timestamp: string;
}

/**
 * Payload sent from the server when the agent shows a scale
 */
export interface ShowScalePayload {
  root: string;
  scaleType: string;
  isChord: boolean;
  timestamp: string;
}

/**
 * Payload sent from the server when the agent clears the fretboard
 */
export interface ClearFretboardPayload {
  timestamp: string;
}

/**
 * Payload sent from the server when the agent animates tab notation
 */
export interface AnimateTabPayload {
  tabText: string;
  timestamp: string;
}

/**
 * Handlers for SignalR events from the agentic chat
 */
export interface AgentConnectionHandlers {
  onShowChord?: (payload: ShowChordPayload) => void;
  onShowScale?: (payload: ShowScalePayload) => void;
  onClearFretboard?: (payload: ClearFretboardPayload) => void;
  onAnimateTab?: (payload: AnimateTabPayload) => void;
}

/**
 * Return type for the useAgentConnection hook
 */
export interface UseAgentConnectionResult {
  /** Whether the SignalR connection is established */
  isConnected: boolean;
  /** Any connection error that occurred */
  connectionError: Error | null;
  /** Manually disconnect from the hub */
  disconnect: () => Promise<void>;
}

/**
 * Hook that establishes a SignalR connection to the fretboard hub
 * for receiving real-time updates from the agentic chat.
 *
 * @param sessionId - The chat session ID to join. Connection won't be established until this is provided.
 * @param handlers - Callback handlers for the different SignalR events
 * @returns Connection status and control methods
 */
export function useAgentConnection(
  sessionId: string | undefined,
  handlers: AgentConnectionHandlers
): UseAgentConnectionResult {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<Error | null>(null);
  const connectionRef = useRef<HubConnection | null>(null);
  const handlersRef = useRef(handlers);

  // Keep handlers ref up to date
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  const disconnect = useCallback(async () => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
      } catch (error) {
        console.warn('[SignalR] Error stopping connection:', error);
      }
      connectionRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    // Don't connect if no session ID
    if (!sessionId) {
      return;
    }

    // Build the hub URL
    const hubUrl = `${API_CONFIG.API_BASE_URL}/hubs/fretboard`;

    // Create the connection
    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: true, // Required for CORS with credentials
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0, 1s, 2s, 4s, 8s, then cap at 30s
          if (retryContext.previousRetryCount === 0) return 0;
          const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount - 1), 30000);
          return delay;
        },
      })
      .configureLogging(LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    // Register event handlers
    connection.on('ShowChord', (payload: ShowChordPayload) => {
      console.log('[SignalR] Received ShowChord:', payload);
      handlersRef.current.onShowChord?.(payload);
    });

    connection.on('ShowScale', (payload: ShowScalePayload) => {
      console.log('[SignalR] Received ShowScale:', payload);
      handlersRef.current.onShowScale?.(payload);
    });

    connection.on('ClearFretboard', (payload: ClearFretboardPayload) => {
      console.log('[SignalR] Received ClearFretboard:', payload);
      handlersRef.current.onClearFretboard?.(payload);
    });

    connection.on('AnimateTab', (payload: AnimateTabPayload) => {
      console.log('[SignalR] Received AnimateTab:', payload);
      handlersRef.current.onAnimateTab?.(payload);
    });

    // Handle connection state changes
    connection.onclose((error) => {
      console.log('[SignalR] Connection closed', error);
      setIsConnected(false);
      if (error) {
        setConnectionError(error);
      }
    });

    connection.onreconnecting((error) => {
      console.log('[SignalR] Reconnecting...', error);
      setIsConnected(false);
    });

    connection.onreconnected(async (connectionId) => {
      console.log('[SignalR] Reconnected:', connectionId);
      setIsConnected(true);
      setConnectionError(null);

      // Rejoin the session group after reconnection
      try {
        await connection.invoke('JoinSession', sessionId);
        console.log('[SignalR] Rejoined session:', sessionId);
      } catch (error) {
        console.error('[SignalR] Failed to rejoin session:', error);
      }
    });

    // Start the connection
    const startConnection = async () => {
      try {
        await connection.start();
        console.log('[SignalR] Connected to fretboard hub');
        setIsConnected(true);
        setConnectionError(null);

        // Join the session group
        await connection.invoke('JoinSession', sessionId);
        console.log('[SignalR] Joined session:', sessionId);
      } catch (error) {
        console.error('[SignalR] Failed to connect:', error);
        setConnectionError(error instanceof Error ? error : new Error(String(error)));
        setIsConnected(false);
      }
    };

    startConnection();

    // Cleanup on unmount or session change
    return () => {
      if (connectionRef.current && connectionRef.current.state !== HubConnectionState.Disconnected) {
        connectionRef.current.stop().catch((err) => {
          console.warn('[SignalR] Error stopping connection during cleanup:', err);
        });
      }
      connectionRef.current = null;
    };
  }, [sessionId]);

  return {
    isConnected,
    connectionError,
    disconnect,
  };
}
